/**
 * ArrowHandle — shared visual primitive for single-axis gizmo arrows.
 *
 * Used by both:
 *   - `OperatorGizmo` for edit-mode operators (extrude, bevel, shrink-fatten).
 *   - `SurfaceFootprintGizmo` for the Object Mode Surface Box / Cylinder height drag.
 *
 * The component draws only the shaft + head pair using the shared
 * `OP_HANDLE_SHAFT` / `OP_HANDLE_HEAD` geometries (lying along +Y in their
 * geometry-local frame). Sizing, orientation, and hit-testing are owned by
 * the parent: the parent is responsible for setting position, applying any
 * orientation rotation, and scaling to the desired screen size. This
 * keeps the primitive pure visual — no per-component state, no per-component
 * pointer handling — so its rendering is identical everywhere it appears.
 */
import { forwardRef } from "react";
import type * as THREE from "three/webgpu";
import { DEFAULT_OPACITY, OP_HANDLE_COLOR } from "./constants.ts";
import { OP_HANDLE_HEAD, OP_HANDLE_SHAFT } from "./gizmoGeometry.ts";

interface ArrowHandleProps {
  /** Override color — defaults to the standard operator-handle yellow. */
  color?: number;
  /** Override opacity — defaults to `DEFAULT_OPACITY`. */
  opacity?: number;
  /** When true, switches to fully opaque hover styling. */
  hovered?: boolean;
  /** Hovered color (only used when `hovered` is true). */
  hoverColor?: number;
}

/**
 * Render the shaft + head meshes inside a `<group>` so the parent can place
 * and orient them as a unit. The forwarded ref points at the wrapping group.
 */
export const ArrowHandle = forwardRef<THREE.Group, ArrowHandleProps>(
  function ArrowHandle({ color, opacity, hovered, hoverColor }, ref) {
    const finalColor = hovered
      ? (hoverColor ?? color ?? OP_HANDLE_COLOR)
      : (color ?? OP_HANDLE_COLOR);
    const finalOpacity = hovered ? 1.0 : (opacity ?? DEFAULT_OPACITY);
    return (
      <group ref={ref}>
        <mesh geometry={OP_HANDLE_SHAFT}>
          <meshBasicMaterial
            color={finalColor}
            transparent
            opacity={finalOpacity}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>
        <mesh geometry={OP_HANDLE_HEAD}>
          <meshBasicMaterial
            color={finalColor}
            transparent
            opacity={finalOpacity}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>
      </group>
    );
  },
);
